import React, { useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  PRE_SITE_ORDER_STATUS,
  reviewNarrative,
  type ProjectDetail,
} from '@tinypro-audit/contracts';
import { ProjectPreSiteReports } from '../components/PreSiteReportLink';
import { ProjectJobAssociations } from './ProjectJobAssociations';
import { Evidence } from './ProjectEvidence';
import { TaskRecords, reviewDate } from './ProjectReviewSupport';
import {
  projectReviewModel,
  topicScope,
  type ReviewTopic,
} from './project-review-model';
import {
  preSiteNextAction,
  preSiteReviewSummary,
} from './project-pre-site-model';

function MissingReviewHistory({
  review,
  data,
  otherTasks,
  reviewedAt,
}: {
  review: ReviewTopic;
  data: ProjectDetail;
  otherTasks: ProjectDetail['workflow']['exceptions'];
  reviewedAt: string | null;
}) {
  const measurement = review.order.result!.measurement;
  const count = review.tasks.length + otherTasks.length;
  return (
    <Accordion variant="default">
      <Accordion.Item value="details">
        <Accordion.Control>
          Details &amp; history
          {count > 0 && ` · ${count} ${count === 1 ? 'record' : 'records'}`}
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md">
            {reviewedAt && (
              <Text size="xs" c="dimmed">
                Reviewed {reviewDate(reviewedAt)}
              </Text>
            )}
            <Text size="sm">{reviewNarrative(measurement.explanation)}</Text>
            <Evidence items={measurement.evidence} />
            {!!measurement.unresolved_python_exceptions.length && (
              <div>
                <Text size="sm" fw={600}>
                  Unresolved pre-site findings
                </Text>
                <ul className="project-short-list">
                  {measurement.unresolved_python_exceptions.map(
                    (finding, i) => (
                      <li key={i}>{finding}</li>
                    ),
                  )}
                </ul>
              </div>
            )}
            {!!review.tasks.length && (
              <div>
                <Text size="sm" fw={600} mb="sm">
                  Review decisions
                </Text>
                <TaskRecords tasks={review.tasks} data={data} />
              </div>
            )}
            {!!otherTasks.length && (
              <div>
                <Text size="sm" fw={600} mb="sm">
                  Other pre-site review decisions
                </Text>
                <TaskRecords tasks={otherTasks} data={data} />
              </div>
            )}
            <ProjectPreSiteReports data={data} />
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function ReviewDetails({
  review,
  data,
}: {
  review: ReviewTopic;
  data: ProjectDetail;
}) {
  const measurement = review.order.result!.measurement;
  const assessments = data.workflow.assessments.filter((assessment) =>
    measurement.assessment_ids.includes(assessment.id),
  );
  const action = preSiteNextAction(review);
  return (
    <Stack gap="md">
      <div>
        <Text fw={600} size="sm">
          {PRE_SITE_ORDER_STATUS[measurement.state]}
        </Text>
        <Text size="sm" c="dimmed">
          {topicScope(review, data)}
        </Text>
      </div>
      {action && (
        <div className="project-next-action">
          <Text fw={600} size="sm" mb="xs">
            Next action
          </Text>
          <Text size="sm">{action}</Text>
        </div>
      )}
      <div className="project-assessment-grid">
        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={600}>Original pre-site form review</Text>
            {!assessments.length && (
              <Text size="sm" c="dimmed">
                No pre-site form review is referenced by this order review.
              </Text>
            )}
            {assessments.map((assessment) => (
              <div key={assessment.id}>
                <Text fw={500}>
                  {typeof assessment.assessment.original_result === 'string'
                    ? assessment.assessment.original_result
                    : JSON.stringify(
                        assessment.assessment.original_result ?? 'Unavailable',
                      )}
                </Text>
                <Text size="sm" c="dimmed">
                  Review {assessment.source_review_id} ·{' '}
                  {reviewDate(
                    typeof assessment.provenance.reviewed_at === 'string'
                      ? assessment.provenance.reviewed_at
                      : null,
                  )}
                </Text>
                <Evidence
                  items={[
                    {
                      kind: 'measurement_assessment',
                      id: assessment.id,
                      page: null,
                      quote: '',
                    },
                  ]}
                />
              </div>
            ))}
          </Stack>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={600}>Order applicability</Text>
            <Text size="sm">
              Does the existing review cover the ordered configuration and
              current form? The original findings and grade are preserved.
            </Text>
            {!!measurement.unresolved_python_exceptions.length && (
              <div>
                <Text size="sm" fw={500}>
                  Unresolved pre-site findings
                </Text>
                <ul className="project-short-list">
                  {measurement.unresolved_python_exceptions.map(
                    (finding, i) => (
                      <li key={i}>{finding}</li>
                    ),
                  )}
                </ul>
              </div>
            )}
          </Stack>
        </Paper>
      </div>
      <Accordion multiple>
        <Accordion.Item value="evidence">
          <Accordion.Control>
            Review notes and supporting evidence
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm">{reviewNarrative(measurement.explanation)}</Text>
              <Evidence items={measurement.evidence} />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="tasks">
          <Accordion.Control>
            Review decisions · {review.tasks.length}{' '}
            {review.tasks.length === 1 ? 'record' : 'records'}
          </Accordion.Control>
          <Accordion.Panel>
            <TaskRecords tasks={review.tasks} data={data} />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

export function ProjectPreSiteReview({
  data,
  model,
  expanded,
  onExpandedChange,
  requestedOrder,
  onHistory,
}: {
  data: ProjectDetail;
  model: ReturnType<typeof projectReviewModel>;
  expanded: boolean;
  onExpandedChange: (value: boolean) => void;
  requestedOrder: string | null;
  onHistory: () => void;
}) {
  const [orderRef, setOrderRef] = useState<string | null>(requestedOrder);
  const missingRequestedOrder = Boolean(
    requestedOrder &&
    !model.preSiteReviews.some(
      (review) => review.order.order_ref === requestedOrder,
    ),
  );
  const selected =
    model.preSiteReviews.find(
      (review) => review.order.order_ref === orderRef,
    ) ??
    model.measurements[0] ??
    model.preSiteReviews[0];
  const summary = preSiteReviewSummary(
    model.preSiteReviews,
    data.workflow.assessments.length,
  );
  const previous = Boolean(model.run && !model.current);
  const compact =
    selected &&
    !missingRequestedOrder &&
    selected.order.result!.measurement.state === 'assessment_unavailable' &&
    !data.workflow.assessments.some((assessment) =>
      selected.order.result!.measurement.assessment_ids.includes(assessment.id),
    );
  const orderSelect = model.preSiteReviews.length > 1 && (
    <Select
      label="Pre-site review for"
      value={selected?.order.order_ref ?? null}
      onChange={(value) => {
        setOrderRef(value);
        onExpandedChange(true);
      }}
      allowDeselect={false}
      data={model.preSiteReviews.map((review) => ({
        value: review.order.order_ref,
        label: `PO ${review.order.identity.po_number ?? review.order.order_ref} · ${PRE_SITE_ORDER_STATUS[review.order.result!.measurement.state]}`,
      }))}
    />
  );
  return (
    <Paper
      component="section"
      withBorder
      radius="md"
      p="lg"
      className="project-presite-card"
      id="project-presite-review"
      tabIndex={-1}
      aria-labelledby="project-presite-title"
    >
      <div className="project-presite-header">
        <div className="project-presite-copy">
          <Group gap="xs" mb={6}>
            <Title order={3} id="project-presite-title" size="h4">
              Pre-site review
            </Title>
            <Badge
              color={previous ? 'gray' : compact ? 'yellow' : summary.color}
              variant="light"
            >
              {previous
                ? 'Previous review'
                : compact
                  ? 'Review not found'
                  : summary.label}
            </Badge>
          </Group>
          {!compact && (
            <Text size="sm" c="dimmed">
              {previous
                ? 'The status below belongs to an earlier analysis. Current order coverage is awaiting review.'
                : summary.description}
            </Text>
          )}
        </div>
        {!compact && (
          <Button
            variant="light"
            onClick={() => onExpandedChange(!expanded)}
            aria-expanded={expanded}
            aria-controls="project-presite-details"
            rightSection={
              expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
            }
          >
            {expanded ? 'Hide details' : 'View details'}
          </Button>
        )}
      </div>
      {compact ? (
        <Stack gap="sm" mt="xs">
          {orderSelect}
          <Text size="sm" c="dimmed">
            PO {selected.order.identity.po_number ?? selected.order.order_ref} ·{' '}
            {topicScope(selected, data)}
          </Text>
          <Text size="sm">
            {previous
              ? 'The previous analysis found no linked pre-site review. Current order coverage is awaiting review.'
              : data.workflow.assessments.length
                ? 'No applicable pre-site review is linked to this order.'
                : 'No completed pre-site review is linked to this project.'}
          </Text>
          <Text size="sm">
            <strong>Next:</strong> Locate the existing review or arrange one.
          </Text>
          <MissingReviewHistory
            key={selected.key}
            review={selected}
            data={data}
            otherTasks={model.preSiteFollowUps}
            reviewedAt={model.run?.completed_at ?? null}
          />
        </Stack>
      ) : (
        <Collapse in={expanded}>
          <div id="project-presite-details" className="project-presite-details">
            <Stack gap="md">
              {previous && (
                <Text size="sm" c="dimmed">
                  Previous analysis:{' '}
                  {reviewDate(model.run?.completed_at ?? null)}
                </Text>
              )}
              {missingRequestedOrder && (
                <Alert
                  color="blue"
                  title="The linked order is not included in this review"
                >
                  Its earlier findings remain in project history.
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    ml="sm"
                    onClick={onHistory}
                  >
                    Open history
                  </Button>
                </Alert>
              )}
              {orderSelect}
              {selected && (
                <>
                  {model.preSiteReviews.length === 1 && (
                    <Text size="sm" c="dimmed">
                      PO{' '}
                      {selected.order.identity.po_number ??
                        selected.order.order_ref}
                    </Text>
                  )}
                  <ReviewDetails
                    key={selected.key}
                    review={selected}
                    data={data}
                  />
                </>
              )}
              {!selected && <Text size="sm">{summary.description}</Text>}
              <ProjectPreSiteReports data={data} />
              {!!model.preSiteFollowUps.length && (
                <Accordion>
                  <Accordion.Item value="other-tasks">
                    <Accordion.Control>
                      Other pre-site review decisions ·{' '}
                      {model.preSiteFollowUps.length}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <TaskRecords tasks={model.preSiteFollowUps} data={data} />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              )}
            </Stack>
          </div>
        </Collapse>
      )}
      <ProjectJobAssociations key={data.project.st_project_id} projectId={data.project.st_project_id} />
    </Paper>
  );
}
